import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { Bell, CheckCircle, Mic, Smartphone, Volume2, Phone, RefreshCw, AlertCircle } from 'lucide-react';
import './CallDeviceSettings.css';
import T, { useAutoTranslate } from '../common/T';

const connectionLabels = {
  ready: 'Mit der Firmenleitung verbunden',
  connecting: 'Verbindung wird aufgebaut',
  reconnecting: 'Verbindung wird wiederhergestellt',
  offline: 'Gerät ist offline',
  error: 'Verbindung prüfen',
  disabled: 'Anrufempfang ausgeschaltet',
};

function StatusRow({ icon: Icon, title, status, ready, children }) {
  return <div className="call-device__row">
    <span className={`call-device__row-icon ${ready ? 'is-ready' : ''}`}><Icon size={18} aria-hidden="true" /></span>
    <div className="call-device__row-text"><strong>{title}</strong><span>{status}</span></div>
    {children || (ready ? <CheckCircle size={19} className="call-device__check" aria-label="Bereit" /> : null)}
  </div>;
}
StatusRow.propTypes = { icon: PropTypes.elementType.isRequired, title: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired, ready: PropTypes.bool, children: PropTypes.node };

export default function CallDeviceSettings({
  preferences, connection, audioReady, microphone, permission, capabilities, server,
  pushState, busy, error, inCall, onEnabled, onAudioTest, onMicrophone, onNotifications,
  onPush, onTestNotification, onReconnect, offer, preview = false, initiallyOpen = false,
}) {
  const at = useAutoTranslate();
  const [open, setOpen] = useState(initiallyOpen);
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener('open-call-device-settings', show);
    return () => window.removeEventListener('open-call-device-settings', show);
  }, []);
  const foregroundReady = preferences.enabled && connection.state === 'ready' && audioReady && microphone === 'granted';
  const backgroundReady = preferences.push && pushState === 'ready';
  const notice = offer?.message;
  let pushDescription = backgroundReady ? 'Hinweise bei ruhendem ERP sind aktiviert'
    : pushState === 'syncing' ? 'Gerät wird angemeldet'
      : preferences.push ? 'Geräte-Anmeldung bitte prüfen' : 'Für dieses Gerät ausgeschaltet';
  if (!server?.enabled) pushDescription = server?.reason === 'loading'
    ? 'Einrichtung wird geprüft' : 'Servereinrichtung noch ausstehend';
  if (capabilities.needsHomeScreen) pushDescription = 'ERP zuerst zum Home-Bildschirm hinzufügen';
  const requestPermissionAvailable = capabilities.notifications && !capabilities.needsHomeScreen;
  return <>
    <button type="button" className={`call-device-trigger ${foregroundReady ? 'is-ready' : ''}`}
      onClick={() => setOpen(true)} aria-label={at('Set up calls on this device')}
      title={at('Set up calls on this device')}>
      <Phone size={17} aria-hidden="true" /><span>{foregroundReady ? at('Calls active') : at('Set up calls')}</span>
      <i aria-hidden="true" />
    </button>
    {notice && <div className="call-device-offer" role="status">
      <Phone size={18} aria-hidden="true" /><span><T>{notice}</T></span>
      {offer.action && <button type="button" onClick={onReconnect}><T>Erneut verbinden</T></button>}
    </div>}
    <Modal show={open} onHide={() => setOpen(false)} centered dialogClassName="call-device-dialog"
      aria-labelledby="call-device-title">
      <Modal.Header closeButton>
        <div className="call-device__heading-icon"><Smartphone size={23} aria-hidden="true" /></div>
        <div><span className="call-device__eyebrow"><T>TELEFONIE · DIESES GERÄT</T></span>
          <Modal.Title id="call-device-title"><T>Überall erreichbar.</T></Modal.Title></div>
      </Modal.Header>
      <Modal.Body>
        {preview && <div className="call-device__preview"><T>Lokale Vorschau mit simulierten Zuständen. Keine Verbindung zur Firmenleitung.</T></div>}
        <p className="call-device__intro"><T>Richte Klingelton, Mikrofon und Hinweise einmal auf deinem Smartphone ein.</T></p>
        <div className="call-device__toggle">
          <div><strong><T>Anrufe auf diesem Gerät empfangen</T></strong><span><T>PC und Smartphone können gleichzeitig klingeln.</T></span></div>
          <input type="checkbox" role="switch" aria-label="Anrufe auf diesem Gerät empfangen"
            checked={preferences.enabled} disabled={busy || inCall} onChange={(e) => onEnabled(e.target.checked)} />
        </div>
        {inCall && <p className="call-device__hint"><T>Während eines Gesprächs bleibt der Empfang eingeschaltet.</T></p>}
        <div className="call-device__status" role="status">
          <i className={connection.state === 'ready' && preferences.enabled ? 'is-ready' : ''} />
          <span>{connectionLabels[preferences.enabled ? connection.state : 'disabled'] || 'Verbindung prüfen'}</span>
          {preferences.enabled && <button type="button" aria-label="Anrufverbindung erneuern"
            onClick={onReconnect} disabled={busy || inCall}><RefreshCw size={15} aria-hidden="true" /></button>}
        </div>
        {connection.reason && <p className="call-device__hint">{connection.reason}</p>}
        <div className="call-device__steps">
          <StatusRow icon={Volume2} title="Klingelton" ready={audioReady}
            status={audioReady ? 'Ton für die aktuelle Browser-Sitzung freigeschaltet' : 'Zum Freischalten einmal antippen'}>
            <button type="button" onClick={onAudioTest} disabled={busy}><T>Ton testen</T></button>
          </StatusRow>
          <StatusRow icon={Mic} title="Mikrofon" ready={microphone === 'granted'}
            status={microphone === 'granted' ? 'Für Gespräche freigegeben' : microphone === 'denied'
              ? 'Im Browser gesperrt · bitte in den Einstellungen erlauben' : 'Freigabe für Gespräche prüfen'}>
            <button type="button" onClick={onMicrophone} disabled={busy || !capabilities.microphone || inCall}>
              {microphone === 'granted' ? 'Prüfen' : 'Freigeben'}</button>
          </StatusRow>
          <StatusRow icon={Bell} title="Benachrichtigungen" ready={permission === 'granted'}
            status={permission === 'granted' ? 'Systemhinweise sind erlaubt' : permission === 'denied'
              ? 'In den Geräteeinstellungen gesperrt' : permission === 'unsupported'
                ? 'In diesem Browser nicht verfügbar' : 'Hinweise ausdrücklich erlauben'}>
            <button type="button" onClick={permission === 'granted' ? onTestNotification : onNotifications}
              disabled={busy || !requestPermissionAvailable || permission === 'denied'}>
              {permission === 'granted' ? 'Testhinweis' : 'Erlauben'}</button>
          </StatusRow>
        </div>
        <div className="call-device__background">
          <div className="call-device__background-title"><Smartphone size={19} aria-hidden="true" />
            <strong><T>Auch bei ruhendem ERP</T></strong>
            <input type="checkbox" role="switch" aria-label="Anrufhinweise im Hintergrund"
              checked={preferences.push} onChange={(e) => onPush(e.target.checked)}
              disabled={busy || !preferences.enabled || !server?.enabled || capabilities.needsHomeScreen
                || !capabilities.push || !capabilities.serviceWorker} />
          </div>
          <p className={backgroundReady ? 'call-device__background-ready' : ''}>{pushDescription}</p>
          <span><T>Der Hinweis öffnet das ERP. Ein noch wartender Anruf wird nach dem Verbinden angeboten.
            Bereits angenommene oder beendete Anrufe werden entsprechend angezeigt.</T></span>
        </div>
        {capabilities.needsHomeScreen && <div className="call-device__install">
          <strong><T>Einmal auf dem iPhone einrichten</T></strong>
          <ol><li><T>Im Browser auf </T><b><T>Teilen</T></b><T> tippen.</T></li><li><b><T>Zum Home-Bildschirm</T></b><T> wählen und als Web-App hinzufügen.</T></li>
            <li><T>ERP über das neue Symbol öffnen, anmelden und Hinweise erlauben.</T></li></ol>
        </div>}
        {!capabilities.secure && <p className="call-device__error" role="alert"><T>Mikrofon und Hinweise benötigen eine sichere HTTPS-Verbindung.</T></p>}
        {error && <div className="call-device__error" role="alert"><AlertCircle size={17} aria-hidden="true" /><span><T>{error}</T></span></div>}
        <p className="call-device__limit"><T>Bei geöffnetem ERP: klingeln und sprechen. Im Hintergrund: Systemhinweis.
          Ton und Zustellung richten sich nach Netz, Stumm- und Fokusmodus. Kein dauerhaftes Telefon-Klingeln bei gesperrtem Bildschirm.</T></p>
      </Modal.Body>
      <Modal.Footer><span><i className={foregroundReady ? 'is-ready' : ''} />{foregroundReady ? 'Empfang im geöffneten ERP bereit' : 'Einrichtung noch nicht vollständig'}</span>
        <button type="button" className="call-device__done" onClick={() => setOpen(false)}><T>Fertig</T></button></Modal.Footer>
    </Modal>
  </>;
}
CallDeviceSettings.propTypes = {
  preferences: PropTypes.object.isRequired, connection: PropTypes.object.isRequired,
  audioReady: PropTypes.bool, microphone: PropTypes.string.isRequired, permission: PropTypes.string.isRequired,
  capabilities: PropTypes.object.isRequired, server: PropTypes.object, pushState: PropTypes.string,
  busy: PropTypes.bool, error: PropTypes.string, inCall: PropTypes.bool, onEnabled: PropTypes.func.isRequired,
  onAudioTest: PropTypes.func.isRequired, onMicrophone: PropTypes.func.isRequired,
  onNotifications: PropTypes.func.isRequired, onPush: PropTypes.func.isRequired,
  onTestNotification: PropTypes.func.isRequired, onReconnect: PropTypes.func.isRequired,
  offer: PropTypes.object, preview: PropTypes.bool, initiallyOpen: PropTypes.bool,
};
